'use client';

import Link from 'next/link';
import Image from '@homeflow/v4-lib/images/image';
import { LuHome } from 'react-icons/lu';
import BLUR_IMAGE from '@homeflow/v4-lib/utils/blur-image';
import { twMerge } from 'tailwind-merge';
import type { PropertyCardProps } from '@homeflow/v4-lib/models';
import RoomIcons from '../../properties/[id]/components/room-icons.component';
import OpenHouseDate from '../../properties/[id]/components/open-house-date.component';
import { CarinaThemeSettings } from '../../theme-settings';
import useThemeSettings from '@homeflow/v4-lib/hooks/use-theme-settings';

export default function PropertyCard({
  property,
  className = '',
  priority = false,
  imageConfig,
  dataPageMarker,
  ...otherProps
}: PropertyCardProps & {
  imageConfig?: { sizes?: string };
}) {
  const themeSettings = useThemeSettings<CarinaThemeSettings>();

  return (
    <div
      className="relative h-full"
      {...(dataPageMarker && { 'data-page-marker': dataPageMarker })}
    >
      <Link
        prefetch={false}
        href={`/properties/${property.id}/${property.primaryChannel}`}
        title={`Property at ${property.displayAddress}`}
        className="h-full block"
        {...otherProps}
      >
        <div
          className={twMerge(
            'carina-property-card relative bg-white flex flex-col group/card h-full w-full justify-stretch',
            className
          )}
        >
          <div className="relative rounded-lg h-72 mb-4 overflow-hidden shrink-0">
            {property.tags.includes('new build for sale') && (
              <div className="absolute top-2 left-2 bg-orange-400 text-white px-2 py-1 z-10 rounded-sm font-bold text-sm">
                New Build For Sale
              </div>
            )}
            {themeSettings?.displayPropertyCardDates.value && (
              <div className="absolute inset-0 top-auto bg-primary text-white text-center py-1 z-10 font-bold">
                <OpenHouseDate tags={property.tags} />
              </div>
            )}

            {property.mainPhoto ? (
              <Image
                className="object-cover w-full bg-gray-200 h-full"
                src={property.mainPhoto}
                alt={`Property at ${property.displayAddress}`}
                fill
                placeholder="blur"
                blurDataURL={BLUR_IMAGE}
                priority={priority}
                sizes="(max-width: 384px) 100vw, 33vw"
                {...(imageConfig?.sizes && { sizes: imageConfig.sizes })}
              />
            ) : (
              <div className="flex flex-col gap-2 items-center justify-center w-full bg-gray-200 h-full">
                <LuHome className="w-20 h-20 opacity-70" />

                <span className="opacity-20 font-bold uppercase">Awaiting images</span>
              </div>
            )}
          </div>

          <div className="flex flex-col justify-between h-full">
            <div className="text-lg font-bold mb-4">{property.displayAddress}</div>

            <div className="flex flex-col justify-between">
              <div className="flex flex-row w-full justify-between items-center mb-4 @sm:mb-0">
                <RoomIcons
                  bedrooms={property.bedrooms}
                  bathrooms={property.bathrooms}
                  className="@md:mb-4"
                />

                <div>
                  <div className="font-bold">
                    {property.priceQualifierFull ? <>{property.priceQualifierFull}</> : <> </>}
                  </div>

                  <div className="text-primary text-3xl font-bold">{property.formattedPrice}</div>
                </div>
              </div>

              <div className="relative h-10 flex items-center justify-start @md:mt-2">
                {property.agencyLogo && (
                  <Image
                    className="object-contain object-left h-10"
                    src={property.agencyLogo}
                    alt={property.agencyName || 'Agent'}
                    width={200}
                    height={50}
                    sizes="200px"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
